import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Ban, Camera, Check as CheckIcon, ExternalLink, Search, SkipForward } from 'lucide-react';
import {
  ALL_REASON_CODES,
  REASON_CODE_LABELS,
  bulkReview,
  fetchCheckManufacturers,
  fetchCheckVendors,
  fetchChecks,
  hasCdkDesc,
  reviewCheck,
} from '../api/checks';
import type { Check, CheckFilters, CheckStatus, ReasonCode, ReviewAction, VendorOption } from '../api/checks';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/ExportButton';
import { HeaderActions } from '../components/HeaderActions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MultiSelect } from '../components/ui/multi-select';
import { FilterSelect } from '../components/ui/filter-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
  selectColumn,
} from '../components/data-table/columns';
import {
  availableColumn,
  checkStatusColumn,
  manufacturerColumn,
  nsIdColumn,
  pctDiffColumn,
  priceDateColumn,
  purchaseCostColumn,
  reasonColumn,
  skuColumn,
  specialPriceColumn,
  vendorColumn,
  vendorCostColumn,
} from '../components/data-table/checkColumns';
import {
  RowActedLabel,
  RowActionButton,
  RowActionMenu,
  RowActions,
  type RowMenuItem,
} from '../components/data-table/RowActions';
import {
  BulkActionBar,
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { detailText } from '../api/products';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDate, fmtVendor } from '../utils/format';
import { takeRows } from '../utils/optimistic';
import { describePartialReview, describeReviewFailure } from '../utils/reviewErrors';
import { useItemDetail } from '../hooks/useItemDetail';
import { useUrlFilters } from '../hooks/useUrlFilters';
import CheckDetailPanel from '../components/CheckDetailPanel';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

// SAFE/UNSAFE check classification (checks.status).
const STATUS_OPTIONS = [
  { value: '',       label: 'All' },
  { value: 'safe',   label: 'Safe',   dot: 'bg-green-500' },
  { value: 'unsafe', label: 'Unsafe', dot: 'bg-red-600'   },
];

export default function ChecksPage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [filters, setFilters] = useState<CheckFilters>({ review_status: 'pending', page: 1, per_page: 50 });
  const [reasonCodes, setReasonCodes] = useState<string[]>([]);
  // Vendor and Status live in the URL so a link (the dashboard's Pending by
  // vendor bars) can open Review pre-filtered, e.g. ?vendor=pet0&vendor=petv.
  const [urlFilters, setUrlFilters, resetUrlFilters] = useUrlFilters({ vendor: [] as string[], status: '' });
  const vendorCodes = urlFilters.vendor;
  const status: CheckStatus | undefined =
    urlFilters.status === 'safe' || urlFilters.status === 'unsafe' ? urlFilters.status : undefined;
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<string[]>([]);

  const [items, setItems]     = useState<Check[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<{ ids: number[]; single: boolean } | null>(null);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    // Matches the design's default view — Manufacturer is available but off, so
    // the table starts at the width that fits a laptop screen.
    manufacturer: false,
  });

  // Row detail is fetched only when a row is opened — list views run to
  // hundreds of rows and this is a per-item query (BOT-392).
  const { detailFor, loadDetail } = useItemDetail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRowSelection({});
    setExpanded({});
    try {
      const data = await fetchChecks({
        ...filters,
        status,
        reason_codes: reasonCodes.length ? (reasonCodes as ReasonCode[]) : undefined,
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        // The API takes a single manufacturer; the control is a multi-select for
        // consistency with the other filters, so send the first pick until the
        // endpoint accepts a list.
        manufacturer: manufacturers[0],
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, status, reasonCodes, vendorCodes, manufacturers]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchCheckVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
    fetchCheckManufacturers().then(setManufacturerOptions).catch(() => setManufacturerOptions([]));
  }, []);

  /** Optimistically remove rows and run the call. On a failure — or when a bulk
   *  call moved fewer rows than were sent — reload from the server instead of
   *  restoring the snapshot: a request the gateway timed out may still land on
   *  the server, so "restored" rows could already be decided (BOT-449). */
  async function runOptimistic(ids: number[], call: () => Promise<unknown>, label: string) {
    const { remaining, snapshot } = takeRows(items, ids);
    setActionError(null);
    setActionNotice(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - snapshot.length);
    setRowSelection({});
    try {
      const updated = await call();
      const partial = typeof updated === 'number'
        ? describePartialReview(label, ids.length, updated)
        : null;
      if (partial) {
        setActionNotice(partial);
        await load();
      }
    } catch (e) {
      setActionError(describeReviewFailure(label, e));
      await load();
    } finally {
      setBusy(false);
    }
  }


  async function handleReview(id: number, action: ReviewAction) {
    if (action === 'disabled') {
      setConfirmDisable({ ids: [id], single: true });
      return;
    }
    await runOptimistic([id], () => reviewCheck(id, action), action === 'approved' ? 'Accept' : 'Skip');
  }

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter(k => rowSelection[k]).map(Number),
    [rowSelection],
  );

  async function handleBulk(action: ReviewAction) {
    if (selectedIds.length === 0) return;
    if (action === 'disabled') {
      setConfirmDisable({ ids: selectedIds, single: false });
      return;
    }
    let ids = selectedIds;
    if (action === 'approved') {
      // Zero-price checks are never approvable — the server refuses them, so
      // leave those rows in place instead of optimistically hiding rows that
      // stay pending.
      const approvable = new Set(items.filter(i => i.vendor_price > 0).map(i => i.id));
      ids = ids.filter(id => approvable.has(id));
      if (ids.length === 0) return;
    }
    await runOptimistic(ids, () => bulkReview(ids, action), action === 'approved' ? 'Bulk accept' : 'Bulk skip');
  }

  async function handleConfirmedDisable() {
    if (!confirmDisable) return;
    const { ids } = confirmDisable;
    setConfirmDisable(null);
    if (ids.length === 1) {
      await runOptimistic(ids, () => reviewCheck(ids[0], 'disabled'), 'Disable');
    } else {
      await runOptimistic(ids, () => bulkReview(ids, 'disabled'), 'Bulk disable');
    }
  }

  function setRange(field: 'pct_diff_min' | 'pct_diff_max' | 'our_price_min' | 'our_price_max', raw: string) {
    setFilters(f => ({ ...f, [field]: raw === '' ? undefined : Number(raw), page: 1 }));
  }

  function resetFilters() {
    resetUrlFilters();
    setReasonCodes([]);
    setManufacturers([]);
    setFilters({ review_status: 'pending', page: 1, per_page: filters.per_page });
  }

  const hasActiveFilters =
    vendorCodes.length > 0 ||
    reasonCodes.length > 0 ||
    manufacturers.length > 0 ||
    !!status ||
    filters.pct_diff_min != null || filters.pct_diff_max != null ||
    filters.our_price_min != null || filters.our_price_max != null ||
    !!filters.price_date_from || !!filters.price_date_to;

  // ── columns ────────────────────────────────────────────────────────────────

  const columns = useMemo<ColumnDef<Check>[]>(() => {
    const cols: ColumnDef<Check>[] = [];

    if (canReview) cols.push(selectColumn<Check>(row => row.original.review_status === 'pending'));
    cols.push(expandColumn<Check>());

    // Shared definitions — SKU first, then the same cells every check view uses.
    cols.push(
      skuColumn,
      vendorColumn,
      manufacturerColumn,
      nsIdColumn,
      reasonColumn,
      availableColumn,
      purchaseCostColumn,
      vendorCostColumn,
      specialPriceColumn,
      pctDiffColumn,
      checkStatusColumn,
      priceDateColumn,
    );

    cols.push({
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const pending = item.review_status === 'pending';

        if (!pending) {
          return (
            <RowActedLabel
              label={item.review_status === 'skipped' ? 'Skipped' : 'Accepted'}
              tone={item.review_status === 'skipped' ? 'muted' : 'success'}
            />
          );
        }

        const menuItems: RowMenuItem[] = [];
        if (canReview) {
          menuItems.push({
            label: 'Disable',
            icon: Ban,
            destructive: true,
            onSelect: () => void handleReview(item.id, 'disabled'),
          });
        }
        menuItems.push({
          label: 'Details',
          icon: Search,
          href: `/all-items/${item.ns_item_id}?from=review&checkId=${item.id}`,
        });
        menuItems.push({
          label: 'Open in NetSuite',
          icon: ExternalLink,
          href: `${NS_BASE_URL}${item.ns_item_id}`,
        });
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot',
            icon: Camera,
            href: item.screenshot_url,
            separatorBefore: true,
          });
        }

        return (
          <RowActions>
            {canReview && item.vendor_price > 0 && (
              <RowActionButton
                label="Accept price"
                icon={CheckIcon}
                tone="success"
                disabled={busy}
                onClick={() => void handleReview(item.id, 'approved')}
              />
            )}
            {canReview && (
              <RowActionButton
                label="Skip for now"
                icon={SkipForward}
                disabled={busy}
                onClick={() => void handleReview(item.id, 'skipped')}
              />
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    });

    return cols;
    // handleReview is stable enough for the row renderers; the list
    // identity that matters here is `items`, which drives `busy` and approvability.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { rowSelection, expanded, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: row => row.original.review_status === 'pending',
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      columnPinning: { left: ['select', 'expand', 'sku'], right: ['actions'] },
    },
  });

  // Kick off the per-row detail fetch as rows open. `expanded === true` is
  // TanStack's "everything expanded" shorthand, which this page never sets.
  useEffect(() => {
    if (expanded === true) return;
    Object.keys(expanded)
      .filter(id => expanded[id])
      .forEach(id => {
        const item = items.find(i => String(i.id) === id);
        if (item) void loadDetail(item.ns_item_id);
      });
  }, [expanded, items, loadDetail]);

  const perPage    = filters.per_page ?? 50;
  const page       = filters.page ?? 1;
  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="checks_review"
          filters={{
            ...filters,
            status,
            page: undefined,
            per_page: undefined,
            reason_codes: reasonCodes.length ? reasonCodes : undefined,
            vendor_codes: vendorCodes.length ? vendorCodes : undefined,
          }}
        />
      </HeaderActions>

      {/* Disable confirm dialog */}
      {confirmDisable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40">
          <div className="w-full max-w-sm rounded-lg border border-border bg-background p-6 shadow-xl">
            <h2 className="mb-2 text-base font-semibold">
              Disable {confirmDisable.single ? 'item' : `${confirmDisable.ids.length} items`}?
            </h2>
            <p className="mb-5 text-[13px] text-muted-foreground">
              This will flag the {confirmDisable.single ? 'item' : 'items'} as disabled in NetSuite and hide{' '}
              {confirmDisable.single ? 'it' : 'them'} from the Magento storefront.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDisable(null)}>Cancel</Button>
              <Button onClick={() => void handleConfirmedDisable()}>Disable</Button>
            </div>
          </div>
        </div>
      )}

      <PageHeading title="Price Checks — Review" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setUrlFilters({ vendor: next }); setFilters(f => ({ ...f, page: 1 })); }}
          />
          <MultiSelect
            label="Reason"
            options={ALL_REASON_CODES.map(rc => ({ value: rc, label: REASON_CODE_LABELS[rc] }))}
            selected={reasonCodes}
            onChange={next => { setReasonCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
            searchPlaceholder="Search reasons…"
          />
          <MultiSelect
            label="Manufacturer"
            options={manufacturerOptions.map(m => ({ value: m, label: m }))}
            selected={manufacturers}
            onChange={next => {
              // Endpoint takes one manufacturer — keep the most recent pick.
              setManufacturers(next.slice(-1));
              setFilters(f => ({ ...f, page: 1 }));
            }}
            searchPlaceholder="Search manufacturers…"
          />
          <FilterSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={status ?? ''}
            onChange={v => {
              setUrlFilters({ status: v });
              setFilters(f => ({ ...f, page: 1 }));
            }}
          />
        </FilterRow>

        <FilterRow className="gap-4">
          <FilterField label="Delta %">
            <Input
              type="number"
              placeholder="min"
              className="w-[68px]"
              value={filters.pct_diff_min ?? ''}
              onChange={e => setRange('pct_diff_min', e.target.value)}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="number"
              placeholder="max"
              className="w-[68px]"
              value={filters.pct_diff_max ?? ''}
              onChange={e => setRange('pct_diff_max', e.target.value)}
            />
          </FilterField>

          <FilterField label="NS cost $">
            <Input
              type="number"
              min={0}
              placeholder="min"
              className="w-[68px]"
              value={filters.our_price_min ?? ''}
              onChange={e => setRange('our_price_min', e.target.value)}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="number"
              min={0}
              placeholder="max"
              className="w-[68px]"
              value={filters.our_price_max ?? ''}
              onChange={e => setRange('our_price_max', e.target.value)}
            />
          </FilterField>

          <FilterField label="Price date">
            <Input
              type="date"
              className="w-[140px]"
              value={filters.price_date_from ?? ''}
              onChange={e => setFilters(f => ({ ...f, price_date_from: e.target.value || undefined, page: 1 }))}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={filters.price_date_to ?? ''}
              onChange={e => setFilters(f => ({ ...f, price_date_to: e.target.value || undefined, page: 1 }))}
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionNotice && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
          <span>{actionNotice}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionNotice(null)} className="text-amber-700">
            Dismiss
          </Button>
        </div>
      )}

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      {canReview && selectedIds.length > 0 && (
        <BulkActionBar count={selectedIds.length} total={total} onClear={() => setRowSelection({})}>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('approved')}>
            <CheckIcon className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
            Accept
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('skipped')}>
            <SkipForward className="h-3.5 w-3.5 text-zinc-500" />
            Skip
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('disabled')}>
            <Ban className="h-3.5 w-3.5 text-zinc-500" />
            Disable
          </Button>
        </BulkActionBar>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No checks found"
        renderExpanded={row => {
          const detail = detailFor(row.original.ns_item_id);
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    [
                      'Description',
                      detailText(detail.data, 'display_name') ??
                        detailText(detail.data, 'name') ??
                        // Last resort only for CDK vendors — for anyone else
                        // this payload field is scraper boilerplate, and an
                        // item with no NS name is better shown as "—".
                        // decision: cdkdesc — see decisions/cdkdesc.md
                        (hasCdkDesc(row.original) ? row.original.cdk_desc : null) ??
                        '—',
                    ],
                    ['Manufacturer', row.original.manufacturer ?? detailText(detail.data, 'manufacturer') ?? '—'],
                    ['Type', detailText(detail.data, 'product_type') ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{row.original.sku ?? '—'}</span>],
                    ['Last checked', fmtDate(row.original.price_date ?? row.original.created_at)],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Price history">
                <CheckDetailPanel state={detail} currentId={row.original.id} />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={row.original.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        selectedCount={selectedIds.length}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
